// /src/data/articleMutationService.js
// Shared article mutation helpers — used by WalkingComposer (and eventually AddEditArticle).
// Single source of truth for PostGIS formatting, sanitisation, and rate-limit handling.

import { supabaseFetch } from '../lib/supabase';

/**
 * Format lat/lng as a PostGIS EWKT point string.
 * CRITICAL: PostGIS uses (longitude, latitude) order.
 * @param {number} lat
 * @param {number} lng
 * @returns {string|null}
 */
export function formatLocationPoint(lat, lng) {
  if (lat == null || lng == null) return null;
  return `SRID=4326;POINT(${lng} ${lat})`;
}

/**
 * Build a sanitised article data object ready for insert.
 * Does NOT resolve home_locality_id or upload images — those are caller responsibilities.
 *
 * @param {Object} params
 * @param {string} params.title
 * @param {string} [params.summary]
 * @param {string} params.content
 * @param {string[]} [params.tags]
 * @param {number|null} [params.lat]
 * @param {number|null} [params.lng]
 * @param {string|null} [params.heroImageDisplayUrl]
 * @param {string|null} [params.heroImageThumbnailUrl]
 * @param {string} [params.heroImageAspect]
 * @param {string|null} [params.heroImageCredit]
 * @param {string|null} [params.imageCaption]
 * @param {Object[]} [params.inlineImages]
 * @param {string} params.authorId
 * @param {string|null} [params.communityId]
 * @param {number|null} [params.homeLocalityId]
 * @param {boolean} [params.isPublished]
 * @returns {Object}
 */
export function buildArticleData({
  title,
  summary,
  content,
  tags,
  lat,
  lng,
  heroImageDisplayUrl,
  heroImageThumbnailUrl,
  heroImageAspect,
  heroImageCredit,
  imageCaption,
  inlineImages,
  authorId,
  communityId,
  homeLocalityId,
  isPublished = true
}) {
  const now = new Date().toISOString();

  return {
    // Saved exactly as typed (#93): safety is at display, never by rewriting the text.
    title: title || 'Untitled',
    summary: summary ? summary : null,
    content: content || '',
    article_type: 'news',
    article_tags: tags && tags.length > 0 ? tags : null,
    latitude: lat || null,
    longitude: lng || null,
    location_point: formatLocationPoint(lat, lng),
    cover_image_url: heroImageDisplayUrl || null,
    hero_image_display_url: heroImageDisplayUrl || null,
    hero_image_thumbnail_url: heroImageThumbnailUrl || null,
    hero_image_aspect: heroImageAspect || '4:3',
    hero_image_credit: heroImageCredit || null,
    image_caption: imageCaption || null,
    inline_images: inlineImages && inlineImages.length > 0 ? inlineImages : [],
    author_id: authorId,
    community_id: communityId || null,
    home_locality_id: homeLocalityId || null,
    is_public: isPublished,
    is_published: isPublished,
    updated_at: now,
    is_platform_content: false
  };
}

/**
 * Insert an article and dispatch the feed-refresh event.
 * Returns { data, error, isRateLimited }.
 *
 * @param {Object} articleData — from buildArticleData()
 * @returns {Promise<{data: Object|null, error: Object|null, isRateLimited: boolean}>}
 */
export async function insertArticle(articleData) {
  const authToken = await supabaseFetch.getAuthToken();
  if (!authToken) {
    console.error('insertArticle: no auth token — session expired, refusing anon insert');
    const err = { message: 'SESSION_EXPIRED', code: 'SESSION_EXPIRED' };
    return { data: null, error: err, isRateLimited: false, isAuthError: true };
  }

  const { data, error } = await supabaseFetch.insert('articles', articleData, { authToken });

  if (error) {
    const isRateLimited = error.message?.includes('rate_limit_exceeded');
    const isAuthError = error.status === 401 || error.status === 403
      || error.message?.includes('JWT')
      || error.message?.includes('policy');
    return { data: null, error, isRateLimited, isAuthError };
  }

  // RLS can silently reject an insert by returning 201 with an empty array.
  // supabaseFetch.insert returns undefined for data in that case.
  if (!data || !data.id) {
    return {
      data: null,
      error: { message: 'Insert rejected — you may need to log in again.', silent: true },
      isRateLimited: false,
      isAuthError: true
    };
  }

  // Notify feed to refresh
  window.dispatchEvent(new CustomEvent('ournet:articlesChanged'));

  return { data, error: null, isRateLimited: false, isAuthError: false };
}

/**
 * Whether a saved post is platform-wide content (is_platform_content), read fresh by id.
 * Edit forms keep a platform post's flag and placement exactly as stored and show a notice
 * (docs/briefs/2026-09-28-edit-forms-truth-and-unsaved-warning-addendum-4.md); only Content
 * Moderation changes either (admin_set_article_platform_content, migration 20260928f). Read by id
 * because the article object a form is opened with may be a feed row without the column.
 *
 * @param {string} articleId
 * @returns {Promise<boolean|null>} null when it could not be read
 */
export async function getIsPlatformContent(articleId) {
  if (!articleId) return false;
  const { data, error } = await supabaseFetch.query('articles', {
    select: 'is_platform_content',
    eq: { id: articleId },
    single: true
  });
  if (error || !data) return null;
  return data.is_platform_content === true;
}
